import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Smartphone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

interface InstallAppButtonProps {
  variant?: "default" | "outline" | "secondary" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  label?: string;
}

const isIos = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent);

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari
    (window.navigator as any).standalone === true);

const InstallAppButton = ({
  variant = "default",
  size = "default",
  className,
  label = "App installieren",
}: InstallAppButtonProps) => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    // iOS Safari unterstützt beforeinstallprompt nicht – Anleitung zeigen
    setShowIosHelp(true);
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        onClick={handleClick}
      >
        <Download className="h-4 w-4 mr-2" />
        {label}
      </Button>

      <Dialog open={showIosHelp} onOpenChange={setShowIosHelp}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Klartech als App installieren
            </DialogTitle>
            <DialogDescription>
              {isIos()
                ? "So fügen Sie Klartech zum Home-Bildschirm hinzu:"
                : "So installieren Sie die App auf Ihrem Gerät:"}
            </DialogDescription>
          </DialogHeader>

          {isIos() ? (
            <ol className="space-y-3 text-sm">
              <li>
                <strong>1.</strong> Tippen Sie unten in Safari auf das{" "}
                <span className="font-mono">Teilen-Symbol</span> (Quadrat mit Pfeil
                nach oben).
              </li>
              <li>
                <strong>2.</strong> Wählen Sie{" "}
                <strong>„Zum Home-Bildschirm"</strong>.
              </li>
              <li>
                <strong>3.</strong> Tippen Sie oben rechts auf{" "}
                <strong>Hinzufügen</strong> – fertig!
              </li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm">
              <li>
                <strong>1.</strong> Öffnen Sie das Browser-Menü (drei Punkte oben
                rechts).
              </li>
              <li>
                <strong>2.</strong> Wählen Sie{" "}
                <strong>„App installieren"</strong> oder{" "}
                <strong>„Zum Startbildschirm hinzufügen"</strong>.
              </li>
              <li>
                <strong>3.</strong> Bestätigen Sie mit <strong>Installieren</strong>.
              </li>
            </ol>
          )}

          <p className="text-xs text-muted-foreground mt-4">
            Funktioniert in modernen Browsern (Safari, Chrome, Edge, Samsung
            Internet). Die App startet danach im Vollbild ohne Browser-Leiste.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppButton;
